import { TRANSFER_POPUP_DONE_LINGER_MS } from "@frontend-shared/constants";
import { EFileUploadStatus } from "@frontend-shared/types";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TransferPopupContext } from "@/contexts/TransferPopupContext";
import { useTransfers } from "@/hooks/use-transfers";

// NOTE: NSAI-7067 — owns what the transfer popup remembers between pages:
// which entries it has let go of, and whether a chat composer is offering it a
// dock. Mounted in the authenticated layout, under TransferProvider.
const TransferPopupProvider = ({ children }: PropsWithChildren): ReactNode => {
  const { transfers } = useTransfers();

  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [dockCount, setDockCount] = useState(0);
  // NOTE: One linger timer per finished upload, keyed by id, so a re-render
  // never restarts a countdown that is already running.
  const lingerTimersRef = useRef(new Map<string, number>());

  const hideEntries = useCallback((entryIds: string[]): void => {
    setHiddenIds((previous) => {
      const next = new Set(previous);
      entryIds.forEach((entryId) => next.add(entryId));
      return next;
    });
  }, []);

  const registerDock = useCallback((): (() => void) => {
    setDockCount((count) => count + 1);

    return () => {
      setDockCount((count) => count - 1);
    };
  }, []);

  // NOTE: A finished upload lingers just long enough to read "Uploaded …",
  // then the popup lets it go. A failure is NOT released on a timer — it stays
  // until the user dismisses it, since an interruption nobody saw is exactly
  // the silent loss this popup exists to prevent.
  useEffect(() => {
    const timers = lingerTimersRef.current;

    for (const entry of transfers) {
      if (
        entry.status !== EFileUploadStatus.DONE ||
        hiddenIds.has(entry.id) ||
        timers.has(entry.id)
      ) {
        continue;
      }

      const timerId = window.setTimeout(() => {
        timers.delete(entry.id);
        hideEntries([entry.id]);
      }, TRANSFER_POPUP_DONE_LINGER_MS);
      timers.set(entry.id, timerId);
    }
  }, [transfers, hiddenIds, hideEntries]);

  useEffect(() => {
    const timers = lingerTimersRef.current;

    return () => {
      timers.forEach((timerId) => window.clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  // NOTE: Forget hidden ids whose entries have left the list (dismissed on the
  // Uploads page, cancelled, purged on a project switch), so the set only ever
  // holds ids that still exist.
  useEffect(() => {
    const currentIds = new Set(transfers.map((entry) => entry.id));
    const hasStaleIds = [...hiddenIds].some(
      (hiddenId) => !currentIds.has(hiddenId),
    );

    if (hasStaleIds) {
      setHiddenIds(
        new Set([...hiddenIds].filter((hiddenId) => currentIds.has(hiddenId))),
      );
    }
  }, [transfers, hiddenIds]);

  const value = useMemo(
    () => ({ hiddenIds, hideEntries, dockCount, registerDock }),
    [hiddenIds, hideEntries, dockCount, registerDock],
  );

  return (
    <TransferPopupContext.Provider value={value}>
      {children}
    </TransferPopupContext.Provider>
  );
};

export default TransferPopupProvider;
